import * as LDClient from 'launchdarkly-js-client-sdk';
import { useState, useEffect } from 'react';

export enum FeatureFlag {
  SUBTENANTS = 'subtenents',
  SNOWFLAKE_SSO = 'snowflake-sso',
  EDIT_SF_SSO_CONFIGURATION = 'edit-sf-sso-configuration',
  TEMPLATE_MANAGEMENT = 'template-management',
  TEMPLATE_UPGRADE = 'template-upgrade',
  CUSTOM_DOMAIN = 'custom-domain',
  FORCE_LEGACY = 'force-legacy',
  PRIVATE_CONNECTOR_VISIBILITY = 'private-connector-visibility',
  AI_ASSISTANT = 'ai-assistant',
  ALLOW_DRILLDOWNS = 'allow-drilldowns',
  ACCESS_ANALYSIS_SECTION = 'access-analysis-section',
  ACCESS_SEMANTIC_SECTION = 'access-semantic-section',
  ENABLE_THENA = 'enable-thena',
  NODE_EXPORTS = 'node-export',
  HIDE_DOCS_LINK = 'hide-docs-link',
  SQUASH_SOURCE = 'squash-source',
  ALLOW_USECASE_MANAGEMENT = 'allow_usecase_management',
  ADVANCED_THREAD_CONTROLS = 'advanced-thread-controls',
}

function getLdClientId(): string | undefined {
  // Prefer env config (works on custom domains)
  const envId = (window as any).__PLB_ENV__?.ld_client_id;
  if (envId) return envId;

  // Fallback for cases where __PLB_ENV__ isn't available yet
  const host = window.location.host;
  if (host.startsWith('local.') || host.startsWith('localhost')) {
    return '67d055939e55d809955930fb';
  } else if (host === 'dev-beta.pliable.co' || host === 'next-dev-beta.pliable.co') {
    return '67db334b41f39109971063f9';
  } else if (host.startsWith('app.') || host.startsWith('next.')) {
    return '67d055939e55d809955930fc';
  }
  return undefined;
}

export default class FeatureFlagService {
  private static instance: FeatureFlagService;
  private client: LDClient.LDClient | null = null;
  private cachedFlags: Map<string, any> = new Map();
  private listeners: Map<string, Set<(newValue: any) => void>> = new Map();
  private _isReady = false;
  private readyPromise: Promise<void>;
  private readyResolve: (() => void) | null = null;

  private constructor() {
    this.readyPromise = new Promise((resolve) => {
      this.readyResolve = resolve;
    });

    const clientId = getLdClientId();
    if (clientId) {
      this.client = LDClient.initialize(clientId, {
        kind: 'user',
        key: 'anonymous',
      });
      this.client.waitForInitialization().then(() => {
        this._isReady = true;
        this.preloadFlags();
        this.client!.on('change', (changes) => {
          for (const flagKey in changes) {
            this.cachedFlags.set(flagKey, changes[flagKey].current);
            this.notifyListeners(flagKey, changes[flagKey].current);
          }
        });
        this.readyResolve?.();
      }).catch(() => {
        this.readyResolve?.();
      });
    } else {
      this.readyResolve?.();
    }
  }

  public static getInstance(): FeatureFlagService {
    if (!FeatureFlagService.instance) {
      FeatureFlagService.instance = new FeatureFlagService();
    }
    return FeatureFlagService.instance;
  }

  identify(user: { key: string; name: string; email: string; tenant: string; role: string }) {
    if (!this.client) return;
    this.client.identify({
      kind: 'user',
      key: user.key,
      name: user.name,
      email: user.email,
      custom: { tenant: user.tenant, role: user.role },
    }).then(() => {
      this.preloadFlags();
      this.notifyAllListeners();
    }).catch((err) => {
      console.error('LaunchDarkly identify error:', err);
    });
  }

  private preloadFlags() {
    if (!this.client) return;
    Object.values(FeatureFlag).forEach((flagName) => {
      try {
        this.cachedFlags.set(flagName, this.client!.variation(flagName, null));
      } catch {
        // ignore
      }
    });
  }

  getFlag<T>(name: string, defaultValue: T): T {
    if (this.cachedFlags.has(name)) {
      return this.cachedFlags.get(name) as T;
    }
    if (!this.client) return defaultValue;
    try {
      const value = this.client.variation(name, defaultValue);
      this.cachedFlags.set(name, value);
      return value;
    } catch {
      return defaultValue;
    }
  }

  isEnabled(name: string): boolean {
    return this.getFlag(name, false);
  }

  get isReady(): boolean {
    return this._isReady;
  }

  waitForInitialization(): Promise<void> {
    return this.readyPromise;
  }

  onFlagChange(name: string, callback: (newValue: any) => void): () => void {
    if (!this.listeners.has(name)) {
      this.listeners.set(name, new Set());
    }
    this.listeners.get(name)!.add(callback);
    return () => {
      this.listeners.get(name)?.delete(callback);
    };
  }

  private notifyListeners(name: string, newValue: any) {
    this.listeners.get(name)?.forEach((cb) => {
      try { cb(newValue); } catch { /* ignore */ }
    });
  }

  private notifyAllListeners() {
    this.listeners.forEach((cbs, flagName) => {
      const value = this.getFlag(flagName, null);
      cbs.forEach((cb) => {
        try { cb(value); } catch { /* ignore */ }
      });
    });
  }
}

export function useFeatureFlag(flagName: string, defaultValue: boolean = false): boolean {
  const service = FeatureFlagService.getInstance();
  const [value, setValue] = useState<boolean>(() => service.getFlag(flagName, defaultValue));

  useEffect(() => {
    let mounted = true;

    service.waitForInitialization().then(() => {
      if (mounted) setValue(service.getFlag(flagName, defaultValue));
    });

    const unsubscribe = service.onFlagChange(flagName, (newValue) => {
      if (mounted) setValue(newValue as boolean);
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [flagName, defaultValue]);

  return value;
}

// Loading-aware variant. Use this when the caller needs to distinguish
// "flag is still loading" from "flag is loaded and false" — e.g. an
// access-control redirect that would otherwise fire on cold load before
// LaunchDarkly has resolved the real value.
export function useFeatureFlagResolved(
  flagName: string,
  defaultValue: boolean = false,
): { value: boolean; isLoading: boolean } {
  const service = FeatureFlagService.getInstance();
  const [value, setValue] = useState<boolean>(() => service.getFlag(flagName, defaultValue));
  const [isLoading, setIsLoading] = useState<boolean>(() => !service.isReady);

  useEffect(() => {
    let mounted = true;

    service.waitForInitialization().then(() => {
      if (mounted) {
        setValue(service.getFlag(flagName, defaultValue));
        setIsLoading(false);
      }
    });

    const unsubscribe = service.onFlagChange(flagName, (newValue) => {
      if (mounted) {
        setValue(newValue as boolean);
        setIsLoading(false);
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [flagName, defaultValue]);

  return { value, isLoading };
}
